import { Link } from 'react-router-dom';
import { PackageSearch } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';

export interface OrderDetailNotFoundProps {
  /** The error that caused the not-found state. Used to distinguish RLS
   *  errors (PGRST116 — always not-found) from transient network errors
   *  (which can be retried). */
  readonly error: unknown;
  /** Fires when the user clicks the Retry button. */
  readonly onRetry: () => void;
}

/**
 * F-022: Error / not-found state for the order detail page.
 *
 * Renders a centered card with a not-found heading, description, a "Back to
 * orders" link (safer than navigate(-1) for unknown/RLS-rejected orders), and
 * a Retry button for genuine network errors.
 *
 * PGRST116 errors are RLS rejections — retrying will always fail. The Retry
 * button is hidden for these. All other errors are shown with a Retry option.
 */
export function OrderDetailNotFound({
  error,
  onRetry,
}: OrderDetailNotFoundProps): React.JSX.Element {
  const { t } = useTranslation();

  // PGRST116 = "no rows" — RLS filtered the row. Retrying won't help.
  const isRlsError =
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    (error as { code: string }).code === 'PGRST116';

  return (
    <div
      data-testid="order-detail-not-found"
      className="mx-auto flex max-w-screen-xl items-start justify-center px-6 py-16"
    >
      <div className="border-border bg-card shadow-admin-card flex flex-col items-center gap-4 rounded-lg border p-10 text-center">
        <PackageSearch
          aria-hidden="true"
          className="text-muted-foreground size-12"
          strokeWidth={1.25}
        />
        <h1
          data-testid="order-not-found-heading"
          className="font-display text-foreground text-xl font-semibold"
        >
          {t('admin.orders.detail.notFound')}
        </h1>
        <p className="text-muted-foreground max-w-sm text-sm">
          {t('admin.orders.detail.notFoundDesc')}
        </p>

        <div className="mt-2 flex flex-wrap justify-center gap-3">
          <Button asChild variant="default">
            <Link to="/orders">{t('admin.orders.detail.backToOrders')}</Link>
          </Button>

          {!isRlsError ? (
            <Button variant="ghost" onClick={onRetry}>
              {t('admin.orders.detail.errorRetry')}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
